// src/pages/courierInvoice/CourierInvoiceDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Download,
  RefreshCw,
  Trash2,
  X,
  Eye,
  Package,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  Filter,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  PieChart,
  Calendar,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  Flag
} from 'lucide-react';
import {
  getInvoiceById,
  getInvoiceOrders,
  getInvoiceSummary,
  getInvoiceDiscrepancies,
  getInvoiceFlags,
  recheckInvoiceFlags,
  resolveInvoiceFlag,
  reprocessFailedOrders,
  cancelInvoice,
  deleteInvoice
} from '../../services/api/courierInvoice';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import DataTable from '../../components/common/DataTable/DataTable';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import AirwaybillTrackingModal from '../orders/components/AirwaybillTrackingModal';
import styles from './CourierInvoiceDetailsPage.module.css';

// Helper: format order items as "ProductName-Qty, ProductName-Qty"
const formatOrderItems = (orderItems) => {
  if (!orderItems || orderItems.length === 0) return 'N/A';
  return orderItems
    .map(item => `${item.Product?.name || 'Unknown'}-${parseFloat(item.quantity) % 1 === 0 ? parseInt(item.quantity) : item.quantity}`)
    .join(', ');
};

const CourierInvoiceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // Data state
  const [invoice, setInvoice] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState('overview');

  // Orders pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalOrders, setTotalOrders] = useState(0);
  const [orderFilters, setOrderFilters] = useState({
    deliveryStatus: '',
    orderMatchStatus: '',
    search: ''
  });

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingRow, setTrackingRow] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: '',
    title: '',
    message: ''
  });

  // Flags
  const [flags, setFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagsPagination, setFlagsPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });
  const [unresolvedFlagCount, setUnresolvedFlagCount] = useState(0);
  const [flagStatusFilter, setFlagStatusFilter] = useState('unresolved');
  const [flagFilters, setFlagFilters] = useState({ deliveryStatus: '', reason: '', search: '' });
  const [rechecking, setRechecking] = useState(false);
  const [resolveModal, setResolveModal] = useState({ show: false, row: null, note: '', submitting: false });

  // Discrepancy groups
  const [expandedGroups, setExpandedGroups] = useState({
    notFound: true,
    alreadyInvoiced: true,
    ineligible: true
  });

  const statusColors = {
    pending: '#f59e0b',
    processing: '#3b82f6',
    completed: '#10b981',
    failed: '#ef4444',
    partially_processed: '#f97316'
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('courierInvoice.pending'),
      processing: t('courierInvoice.processing'),
      completed: t('courierInvoice.completed'),
      failed: t('courierInvoice.failed'),
      partially_processed: t('courierInvoice.partiallyProcessed')
    };
    return labels[status] || status;
  };

  const matchStatusColors = {
    matched: '#10b981',
    not_found: '#ef4444',
    already_invoiced: '#f59e0b',
    ineligible: '#6b7280',
    pending: '#3b82f6'
  };

  const getMatchStatusLabel = (status) => {
    const labels = {
      matched: t('courierInvoice.orderMatched'),
      not_found: t('courierInvoice.orderNotFound'),
      already_invoiced: t('courierInvoice.orderAlreadyInvoiced'),
      ineligible: t('courierInvoice.orderIneligible'),
      pending: t('courierInvoice.pending')
    };
    return labels[status] || status;
  };

  // Load invoice data on mount
  useEffect(() => {
    loadInvoiceData();
  }, [id]);

  // Load orders when tab or filters change
  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, currentPage, pageSize, orderFilters]);

  // Load discrepancies when tab changes
  useEffect(() => {
    if (activeTab === 'discrepancies' && discrepancies.length === 0) {
      loadDiscrepancies();
    }
  }, [activeTab]);

  // Load flags on mount (for tab count) and when the flags filter or page changes
  useEffect(() => {
    loadFlags();
  }, [id, flagStatusFilter, flagFilters, flagsPagination.currentPage, flagsPagination.pageSize]);

  const handleFlagStatusChange = (value) => {
    setFlagStatusFilter(value || 'unresolved');
    setFlagsPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFlagFilterChange = (field, value) => {
    setFlagFilters(prev => ({ ...prev, [field]: value || '' }));
    setFlagsPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const loadFlags = async () => {
    try {
      setFlagsLoading(true);
      const response = await getInvoiceFlags(id, {
        status: flagStatusFilter,
        ...flagFilters,
        page: flagsPagination.currentPage,
        limit: flagsPagination.pageSize
      });
      setFlags(response.flags || []);
      setUnresolvedFlagCount(response.unresolvedCount || 0);
      setFlagsPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error('Failed to load flags:', error);
      showError(t('courierInvoice.failedToLoadFlags'));
    } finally {
      setFlagsLoading(false);
    }
  };

  const handleRecheckFlags = async () => {
    try {
      setRechecking(true);
      const result = await recheckInvoiceFlags(id);
      success(t('courierInvoice.flagsRechecked', { checked: result.checked, flagged: result.flagged }));
      await loadFlags();
    } catch (error) {
      console.error('Failed to recheck flags:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToRecheckFlags'));
    } finally {
      setRechecking(false);
    }
  };

  const closeResolveModal = () => setResolveModal({ show: false, row: null, note: '', submitting: false });

  const handleResolveFlag = async () => {
    if (!resolveModal.note.trim()) return;
    try {
      setResolveModal(prev => ({ ...prev, submitting: true }));
      await resolveInvoiceFlag(id, resolveModal.row.id, resolveModal.note.trim());
      success(t('courierInvoice.flagResolved'));
      closeResolveModal();
      await loadFlags();
    } catch (error) {
      console.error('Failed to resolve flag:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToResolveFlag'));
      setResolveModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const formatFlagValue = (reason, value) => {
    if (value === null || value === undefined) return '—';
    return reason.type === 'weight_mismatch' ? `${value} kg` : formatCurrency(value);
  };

  const openOrderModal = (row) => {
    setSelectedOrder(row);
    setShowOrderModal(true);
  };

  const stopPropagation = (e) => e.stopPropagation();

  const renderAirwaybill = (row) => row.EcomOrder ? (
    <button
      type="button"
      className={styles.airwaybillButton}
      title={t('ecomOrders.viewPackageTimeline')}
      onClick={(e) => {
        e.stopPropagation();
        setTrackingRow(row);
      }}
    >
      {row.airwaybillNumber}
    </button>
  ) : (
    <span className={styles.airwaybill}>{row.airwaybillNumber}</span>
  );

  const renderOrderLink = (row) => row.EcomOrder ? (
    <Link to={`/orders/${row.EcomOrder.id}`} className={styles.orderLink} target="_blank" onClick={stopPropagation}>
      {row.EcomOrder.orderNumber}
      <ExternalLink size={14} />
    </Link>
  ) : (
    <span className={styles.naText}>N/A</span>
  );

  const renderOrderStatus = (row) => row.EcomOrder?.OrderStatus ? (
    <span
      className={styles.orderStatusBadge}
      style={{
        backgroundColor: `${row.EcomOrder.OrderStatus.colorCode}20`,
        color: row.EcomOrder.OrderStatus.colorCode,
        borderColor: `${row.EcomOrder.OrderStatus.colorCode}40`
      }}
    >
      {row.EcomOrder.OrderStatus.label}
    </span>
  ) : (
    <span className={styles.naText}>N/A</span>
  );

  const renderOrderItems = (row) => (
    <span className={styles.itemsCell} title={formatOrderItems(row.EcomOrder?.orderItems)}>
      {formatOrderItems(row.EcomOrder?.orderItems)}
    </span>
  );

  const renderCreatedAt = (row) => row.EcomOrder?.createdAt
    ? formatDateTime(row.EcomOrder.createdAt)
    : <span className={styles.naText}>N/A</span>;

  const flagColumns = [
    { key: 'rowNumber', header: t('courierInvoice.rowNumber'), width: '80px' },
    { key: 'airwaybillNumber', header: t('courierInvoice.airwaybill'), render: renderAirwaybill },
    { key: 'order', header: t('courierInvoice.order'), render: renderOrderLink },
    {
      key: 'deliveryStatus',
      header: t('courierInvoice.deliveryStatus'),
      render: (row) => <span className={styles.deliveryBadge}>{row.deliveryStatus}</span>
    },
    {
      key: 'flagReasons',
      header: t('courierInvoice.flagReasonsColumn'),
      render: (row) => (row.flagReasons || []).map((reason, index) => (
        <div key={index} className={styles.flagReasonLine}>
          <strong>{t(`courierInvoice.flagReason_${reason.type}`)}:</strong>{' '}
          {reason.type === 'check_error'
            ? reason.actual
            : t('courierInvoice.flagExpectedVsInvoice', {
                expected: formatFlagValue(reason, reason.expected),
                actual: formatFlagValue(reason, reason.actual)
              })}
        </div>
      ))
    },
    {
      key: 'resolveNote',
      header: t('courierInvoice.resolveNoteColumn'),
      width: '240px',
      render: (row) => row.flagResolvedAt ? (
        <div className={styles.resolveNoteCell}>
          <span className={styles.resolveNoteText} title={row.flagResolveNote}>
            {row.flagResolveNote}
          </span>
          <span className={styles.resolveMeta}>
            <span className={styles.resolveMetaUser}>{row.flagResolvedBy?.name || '—'}</span>
            <span>{formatDateTime(row.flagResolvedAt)}</span>
          </span>
        </div>
      ) : (
        <span className={styles.naText}>—</span>
      )
    },
    {
      key: 'customerName',
      header: t('courierInvoice.customerName'),
      render: (row) => row.EcomOrder?.customerName || <span className={styles.naText}>N/A</span>
    },
    {
      key: 'shop',
      header: t('courierInvoice.shop'),
      render: (row) => row.EcomOrder?.Shop?.name || <span className={styles.naText}>N/A</span>
    },
    { key: 'orderStatus', header: t('courierInvoice.orderStatus'), render: renderOrderStatus },
    { key: 'items', header: t('courierInvoice.items'), render: renderOrderItems },
    {
      key: 'weight',
      header: t('courierInvoice.weight'),
      render: (row) => row.EcomOrder?.weight ?? <span className={styles.naText}>—</span>
    },
    {
      key: 'codAmount',
      header: t('courierInvoice.codAmount'),
      render: (row) => row.EcomOrder ? formatCurrency(row.EcomOrder.codAmount) : <span className={styles.naText}>N/A</span>
    },
    { key: 'createdAt', header: t('courierInvoice.createdDate'), className: styles.dateCell, render: renderCreatedAt },
    {
      key: 'createdBy',
      header: t('courierInvoice.createdBy'),
      render: (row) => row.EcomOrder?.createdBy?.name || <span className={styles.naText}>N/A</span>
    },
    {
      key: 'confirmedBy',
      header: t('courierInvoice.confirmedBy'),
      render: (row) => row.EcomOrder?.confirmedByUser?.name || <span className={styles.naText}>—</span>
    }
  ];

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      const [invoiceRes, summaryRes] = await Promise.all([
        getInvoiceById(id),
        getInvoiceSummary(id)
      ]);
      
      setInvoice(invoiceRes.invoice);
      setSummary(summaryRes.summary);
    } catch (error) {
      console.error('Failed to load invoice data:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToLoadInvoice'));
      navigate('/courier-invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
        ...orderFilters
      };

      const response = await getInvoiceOrders(id, params);
      setOrders(response.orders || []);
      setTotalOrders(response.total || 0);
    } catch (error) {
      console.error('Failed to load orders:', error);
      showError(t('courierInvoice.failedToLoadOrders'));
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadDiscrepancies = async () => {
    try {
      const response = await getInvoiceDiscrepancies(id);
      setDiscrepancies(response.discrepancies || []);
    } catch (error) {
      console.error('Failed to load discrepancies:', error);
      showError(t('courierInvoice.failedToLoadDiscrepancies'));
    }
  };

  const handleFilterChange = (field, value) => {
    setOrderFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleReprocessAll = async () => {
    setConfirmModal({
      show: true,
      type: 'reprocess',
      title: t('courierInvoice.reprocessFailedOrders'),
      message: t('courierInvoice.reprocessFailedConfirm')
    });
  };

  const handleReprocessGroup = async (groupType) => {
    const groupOrders = discrepancies.filter(d => d.orderMatchStatus === groupType);
    const orderIds = groupOrders.map(d => d.id);

    try {
      const results = await reprocessFailedOrders(id, orderIds);
      success(t('courierInvoice.ordersReprocessedSuccess', { count: results.successCount }));
      
      await Promise.all([
        loadInvoiceData(),
        loadDiscrepancies(),
        activeTab === 'orders' && loadOrders()
      ]);
    } catch (error) {
      console.error('Failed to reprocess orders:', error);
      showError(error.response?.data?.message || t('courierInvoice.failedToReprocessOrders'));
    }
  };

  const handleConfirmAction = async () => {
    try {
      if (confirmModal.type === 'reprocess') {
        const results = await reprocessFailedOrders(id);
        success(t('courierInvoice.ordersReprocessedSuccess', { count: results.successCount }));
        await Promise.all([
          loadInvoiceData(),
          loadDiscrepancies(),
          activeTab === 'orders' && loadOrders()
        ]);
      } else if (confirmModal.type === 'cancel') {
        await cancelInvoice(id);
        success(t('courierInvoice.invoiceCancelledSuccess'));
        navigate('/courier-invoices');
      } else if (confirmModal.type === 'delete') {
        await deleteInvoice(id);
        success(t('courierInvoice.invoiceDeletedSuccess'));
        navigate('/courier-invoices');
      }
      setConfirmModal({ show: false, type: '', title: '', message: '' });
    } catch (error) {
      console.error('Action failed:', error);
      showError(error.response?.data?.message || t('courierInvoice.actionFailed'));
    }
  };

  const downloadErrorReport = () => {
    const csvContent = [
      [t('courierInvoice.rowNumber'), t('courierInvoice.airwaybillNumber'), t('courierInvoice.matchStatus'), t('courierInvoice.errorMessage')],
      ...discrepancies.map(d => [
        d.rowNumber || '',
        d.airwaybillNumber || '',
        getMatchStatusLabel(d.orderMatchStatus) || '',
        d.errorMessage || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice?.invoiceNumber}-discrepancies.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadOrdersList = () => {
    const csvContent = [
      [
        t('courierInvoice.rowNumber'),
        t('courierInvoice.airwaybillNumber'),
        t('courierInvoice.orderNumber'),
        t('courierInvoice.customerName'),
        t('courierInvoice.shop'),
        t('courierInvoice.orderStatus'),
        t('courierInvoice.items'),
        t('courierInvoice.createdDate'),
        t('courierInvoice.createdBy'),
        t('courierInvoice.confirmedBy'),
        t('courierInvoice.codAmount'),
        t('courierInvoice.courierCharge'),
        t('courierInvoice.amountPayable'),
        t('courierInvoice.deliveryStatus'),
        t('courierInvoice.matchStatus')
      ],
      ...orders.map(order => [
        order.rowNumber || '',
        order.airwaybillNumber || '',
        order.EcomOrder?.orderNumber || 'N/A',
        order.EcomOrder?.customerName || 'N/A',
        order.EcomOrder?.Shop?.name || 'N/A',
        order.EcomOrder?.OrderStatus?.label || 'N/A',
        formatOrderItems(order.EcomOrder?.orderItems),
        order.EcomOrder?.createdAt || 'N/A',
        order.EcomOrder?.createdBy?.name || 'N/A',
        order.EcomOrder?.confirmedByUser?.name || 'N/A',
        order.codAmount || 0,
        order.courierCharge || 0,
        order.amountPayable || 0,
        order.deliveryStatus || '',
        getMatchStatusLabel(order.orderMatchStatus) || ''
      ])
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice?.invoiceNumber}-orders.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const canCancel = invoice && ['pending', 'processing'].includes(invoice.status);
  const canDelete = invoice && ['failed', 'pending'].includes(invoice.status);
  const canReprocess = invoice && invoice.status === 'partially_processed';

  const totalPages = Math.ceil(totalOrders / pageSize);

  const groupedDiscrepancies = {
    notFound: discrepancies.filter(d => d.orderMatchStatus === 'not_found'),
    alreadyInvoiced: discrepancies.filter(d => d.orderMatchStatus === 'already_invoiced'),
    ineligible: discrepancies.filter(d => d.orderMatchStatus === 'ineligible')
  };

  if (loading) {
    return (
      <div className={styles.detailsPage}>
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinner} size={48} />
          <p>{t('courierInvoice.loadingInvoiceDetails')}</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={styles.detailsPage}>
        <div className={styles.emptyState}>
          <AlertCircle size={64} className={styles.emptyIcon} />
          <h3>{t('courierInvoice.invoiceNotFound')}</h3>
          <button onClick={() => navigate('/courier-invoices')} className={styles.primaryButton}>
            {t('courierInvoice.backToInvoices')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailsPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <button onClick={() => navigate('/courier-invoices')} className={styles.backButton}>
            <ArrowLeft size={18} />
            {t('courierInvoice.backToInvoices')}
          </button>
        </div>
        
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <FileText className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('courierInvoice.invoiceTitle', { number: invoice.invoiceNumber })}</h1>
              <div className={styles.headerMeta}>
                <span className={styles.courierName}>
                  {invoice.courierAccount?.CourierCompany?.name}
                </span>
                <span className={styles.separator}>•</span>
                <span>{invoice.invoiceDate}</span>
                <span className={styles.separator}>•</span>
                <span>{t('courierInvoice.uploadedByUser', { user: invoice.uploadedBy?.name })}</span>
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            <span
              className={styles.statusBadge}
              style={{ 
                backgroundColor: `${statusColors[invoice.status]}20`, 
                color: statusColors[invoice.status] 
              }}
            >
              {getStatusLabel(invoice.status)}
            </span>

            <button className={styles.secondaryButton}>
              <Download size={18} />
              {t('courierInvoice.downloadPDF')}
            </button>

            {canReprocess && hasPermission('reprocess_courier_invoice') && (
              <button onClick={handleReprocessAll} className={styles.secondaryButton}>
                <RefreshCw size={18} />
                {t('courierInvoice.reprocessFailed')}
              </button>
            )}

            {canCancel && hasPermission('create_courier_invoice') && (
              <button
                onClick={() => setConfirmModal({
                  show: true,
                  type: 'cancel',
                  title: t('courierInvoice.cancelInvoiceTitle'),
                  message: t('courierInvoice.cancelInvoiceConfirm')
                })}
                className={styles.warningButton}
              >
                <X size={18} />
                {t('courierInvoice.cancel')}
              </button>
            )}

            {canDelete && hasPermission('delete_courier_invoice') && (
              <button
                onClick={() => setConfirmModal({
                  show: true,
                  type: 'delete',
                  title: t('courierInvoice.deleteInvoiceTitle'),
                  message: t('courierInvoice.deleteInvoiceConfirm')
                })}
                className={styles.dangerButton}
              >
                <Trash2 size={18} />
                {t('courierInvoice.delete')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <Package size={24} className={styles.cardIcon} />
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('courierInvoice.totalOrders')}</span>
            <span className={styles.cardValue}>{summary?.totalOrders || 0}</span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <CheckCircle size={24} className={styles.successIcon} />
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('courierInvoice.matchedOrders')}</span>
            <span className={styles.cardValue}>
              {summary?.matchedOrders || 0}
              {summary?.totalOrders > 0 && (
                <span className={styles.percentage}>
                  ({Math.round((summary.matchedOrders / summary.totalOrders) * 100)}%)
                </span>
              )}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <AlertTriangle 
            size={24} 
            className={(summary?.notFoundOrders + summary?.alreadyInvoicedOrders + summary?.ineligibleOrders) > 0 ? styles.warningIcon : styles.cardIcon} 
          />
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('courierInvoice.discrepancies')}</span>
            <span className={styles.cardValue}>
              {(summary?.notFoundOrders || 0) + (summary?.alreadyInvoicedOrders || 0) + (summary?.ineligibleOrders || 0)}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <DollarSign size={24} className={styles.cardIcon} />
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('courierInvoice.netAmountPayable')}</span>
            <span className={`${styles.cardValue} ${summary?.totalAmountPayable >= 0 ? styles.positive : styles.negative}`}>
              {formatCurrency(summary?.totalAmountPayable || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className={styles.tabsSection}>
        <div className={styles.tabs}>
          <button
            onClick={() => setActiveTab('overview')}
            className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
          >
            <BarChart3 size={18} />
            {t('courierInvoice.overview')}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`${styles.tab} ${activeTab === 'orders' ? styles.active : ''}`}
          >
            <Package size={18} />
            {t('courierInvoice.ordersTab', { count: summary?.totalOrders || 0 })}
          </button>
          <button
            onClick={() => setActiveTab('discrepancies')}
            className={`${styles.tab} ${activeTab === 'discrepancies' ? styles.active : ''}`}
          >
            <AlertTriangle size={18} />
            {t('courierInvoice.discrepanciesTab', { count: (summary?.notFoundOrders || 0) + (summary?.alreadyInvoicedOrders || 0) + (summary?.ineligibleOrders || 0) })}
          </button>
          <button
            onClick={() => setActiveTab('flags')}
            className={`${styles.tab} ${activeTab === 'flags' ? styles.active : ''}`}
          >
            <Flag size={18} />
            {t('courierInvoice.flagsTab', { count: unresolvedFlagCount })}
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`${styles.tab} ${activeTab === 'timeline' ? styles.active : ''}`}
          >
            <Clock size={18} />
            {t('courierInvoice.timeline')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className={styles.overviewTab}>
            <div className={styles.chartsGrid}>
              <div className={styles.chartCard}>
                <h3>
                  <PieChart size={18} />
                  {t('courierInvoice.deliveryBreakdown')}
                </h3>
                <div className={styles.statsGrid}>
                  <div className={styles.statItem}>
                    <TrendingUp className={styles.successIcon} size={20} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.delivered')}</span>
                      <span className={styles.statValue}>{summary?.deliveredOrders || 0}</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <TrendingDown className={styles.warningIcon} size={20} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.returned')}</span>
                      <span className={styles.statValue}>{summary?.returnedOrders || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.chartCard}>
                <h3>
                  <BarChart3 size={18} />
                  {t('courierInvoice.matchStatusBreakdown')}
                </h3>
                <div className={styles.statsGrid}>
                  <div className={styles.statItem}>
                    <div className={styles.statIndicator} style={{ backgroundColor: matchStatusColors.matched }} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.matched')}</span>
                      <span className={styles.statValue}>{summary?.matchedOrders || 0}</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <div className={styles.statIndicator} style={{ backgroundColor: matchStatusColors.not_found }} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.notFound')}</span>
                      <span className={styles.statValue}>{summary?.notFoundOrders || 0}</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <div className={styles.statIndicator} style={{ backgroundColor: matchStatusColors.already_invoiced }} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.alreadyInvoiced')}</span>
                      <span className={styles.statValue}>{summary?.alreadyInvoicedOrders || 0}</span>
                    </div>
                  </div>
                  <div className={styles.statItem}>
                    <div className={styles.statIndicator} style={{ backgroundColor: matchStatusColors.ineligible }} />
                    <div>
                      <span className={styles.statLabel}>{t('courierInvoice.ineligible')}</span>
                      <span className={styles.statValue}>{summary?.ineligibleOrders || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.financialCard}>
              <h3>
                <DollarSign size={18} />
                {t('courierInvoice.financialBreakdown')}
              </h3>
              <div className={styles.financialTable}>
                <div className={styles.financialRow}>
                  <span>{t('courierInvoice.totalCodCollected')}:</span>
                  <strong>{formatCurrency(summary?.totalCodCollected || 0)}</strong>
                </div>
                <div className={styles.financialRow}>
                  <span>{t('courierInvoice.totalCourierCharges')}:</span>
                  <strong className={styles.negative}>{formatCurrency(summary?.totalCourierCharges || 0)}</strong>
                </div>
                <div className={`${styles.financialRow} ${styles.total}`}>
                  <span>{t('courierInvoice.netAmountPayable')}:</span>
                  <strong className={summary?.totalAmountPayable >= 0 ? styles.positive : styles.negative}>
                    {formatCurrency(summary?.totalAmountPayable || 0)}
                  </strong>
                </div>
              </div>
            </div>

            {invoice.metadata && (
              <div className={styles.metadataCard}>
                <h3>
                  <FileText size={18} />
                  {t('courierInvoice.processingMetadata')}
                </h3>
                <div className={styles.metadataGrid}>
                  {invoice.metadata.fileName && (
                    <div className={styles.metadataItem}>
                      <span className={styles.metadataLabel}>{t('courierInvoice.fileName')}:</span>
                      <span className={styles.metadataValue}>{invoice.metadata.fileName}</span>
                    </div>
                  )}
                  {invoice.metadata.fileSize && (
                    <div className={styles.metadataItem}>
                      <span className={styles.metadataLabel}>{t('courierInvoice.fileSize')}:</span>
                      <span className={styles.metadataValue}>
                        {(invoice.metadata.fileSize / 1024).toFixed(2)} KB
                      </span>
                    </div>
                  )}
                  {invoice.metadata.totalRowsInFile && (
                    <div className={styles.metadataItem}>
                      <span className={styles.metadataLabel}>{t('courierInvoice.rowsInFile')}:</span>
                      <span className={styles.metadataValue}>{invoice.metadata.totalRowsInFile}</span>
                    </div>
                  )}
                  {invoice.metadata.processingStartTime && invoice.metadata.processingEndTime && (
                    <div className={styles.metadataItem}>
                      <span className={styles.metadataLabel}>{t('courierInvoice.processingTime')}:</span>
                      <span className={styles.metadataValue}>
                        {Math.round((new Date(invoice.metadata.processingEndTime) - new Date(invoice.metadata.processingStartTime)) / 1000)} {t('courierInvoice.seconds')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className={styles.ordersTab}>
            <div className={styles.ordersFilters}>
              <div className={styles.filterGroup}>
                <Dropdown
                  value={orderFilters.deliveryStatus || null}
                  onChange={(value) => handleFilterChange('deliveryStatus', value || '')}
                  options={[
                    { value: 'delivered', label: t('courierInvoice.deliveryDelivered') },
                    { value: 'returned', label: t('courierInvoice.deliveryReturned') },
                    { value: 'in_transit', label: t('courierInvoice.deliveryInTransit') },
                    { value: 'other', label: t('courierInvoice.deliveryOther') }
                  ]}
                  placeholder={t('courierInvoice.allDeliveryStatuses')}
                />
              </div>

              <div className={styles.filterGroup}>
                <Dropdown
                  value={orderFilters.orderMatchStatus || null}
                  onChange={(value) => handleFilterChange('orderMatchStatus', value || '')}
                  options={[
                    { value: 'matched', label: t('courierInvoice.orderMatched') },
                    { value: 'not_found', label: t('courierInvoice.orderNotFound') },
                    { value: 'already_invoiced', label: t('courierInvoice.orderAlreadyInvoiced') },
                    { value: 'ineligible', label: t('courierInvoice.orderIneligible') }
                  ]}
                  placeholder={t('courierInvoice.allMatchStatuses')}
                />
              </div>

              <div className={styles.searchBox}>
                <Search className={styles.searchIcon} size={18} />
                <input
                  type="text"
                  placeholder={t('courierInvoice.searchByAirwaybill')}
                  value={orderFilters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <button onClick={downloadOrdersList} className={styles.secondaryButton}>
                <FileSpreadsheet size={18} />
                {t('courierInvoice.exportOrders')}
              </button>
            </div>

            {ordersLoading ? (
              <div className={styles.loadingState}>
                <Loader2 className={styles.spinner} size={32} />
                <p>{t('courierInvoice.loadingOrders')}</p>
              </div>
            ) : orders.length === 0 ? (
              <div className={styles.emptyState}>
                <Package size={48} className={styles.emptyIcon} />
                <h3>{t('courierInvoice.noOrdersFound')}</h3>
                <p>{t('courierInvoice.noOrdersMatchFilters')}</p>
              </div>
            ) : (
              <>
                <div className={styles.tableContainer}>
                  <table className={styles.ordersTable}>
                    <thead>
                      <tr>
                        <th>{t('courierInvoice.rowNumber')}</th>
                        <th>{t('courierInvoice.airwaybillNumber')}</th>
                        <th>{t('courierInvoice.orderNumber')}</th>
                        <th>{t('courierInvoice.customerName')}</th>
                        <th>{t('courierInvoice.shop')}</th>
                        <th>{t('courierInvoice.orderStatus')}</th>
                        <th>{t('courierInvoice.items')}</th>
                        <th>{t('courierInvoice.createdDate')}</th>
                        <th>{t('courierInvoice.createdBy')}</th>
                        <th>{t('courierInvoice.confirmedBy')}</th>
                        <th>{t('courierInvoice.codAmount')}</th>
                        <th>{t('courierInvoice.courierCharge')}</th>
                        <th>{t('courierInvoice.amountPayable')}</th>
                        <th>{t('courierInvoice.deliveryStatus')}</th>
                        <th>{t('courierInvoice.matchStatus')}</th>
                        <th>{t('courierInvoice.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id} onClick={() => openOrderModal(order)} className={styles.clickableRow}>
                          <td>{order.rowNumber}</td>
                          <td>{renderAirwaybill(order)}</td>
                          <td>{renderOrderLink(order)}</td>
                          <td>{order.EcomOrder?.customerName || 'N/A'}</td>
                          <td>{order.EcomOrder?.Shop?.name || 'N/A'}</td>
                          <td>{renderOrderStatus(order)}</td>
                          <td>{renderOrderItems(order)}</td>
                          <td className={styles.dateCell}>{renderCreatedAt(order)}</td>
                          <td>{order.EcomOrder?.createdBy?.name || <span className={styles.naText}>N/A</span>}</td>
                          <td>{order.EcomOrder?.confirmedByUser?.name || <span className={styles.naText}>—</span>}</td>
                          <td>{formatCurrency(order.codAmount)}</td>
                          <td className={styles.negative}>{formatCurrency(order.courierCharge)}</td>
                          <td className={order.amountPayable >= 0 ? styles.positive : styles.negative}>
                            {formatCurrency(order.amountPayable)}
                          </td>
                          <td>
                            <span className={styles.deliveryBadge}>
                              {order.deliveryStatus}
                            </span>
                          </td>
                          <td>
                            <span
                              className={styles.matchBadge}
                              style={{ 
                                backgroundColor: `${matchStatusColors[order.orderMatchStatus]}20`,
                                color: matchStatusColors[order.orderMatchStatus]
                              }}
                              title={order.errorMessage || ''}
                            >
                              {getMatchStatusLabel(order.orderMatchStatus)}
                            </span>
                          </td>
                          <td>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openOrderModal(order);
                              }}
                              className={styles.actionButton}
                              title={t('courierInvoice.viewDetails')}
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <div className={styles.paginationInfo}>
                    {t('courierInvoice.showingOrders', {
                      from: ((currentPage - 1) * pageSize) + 1,
                      to: Math.min(currentPage * pageSize, totalOrders),
                      total: totalOrders
                    })}
                  </div>

                  <div className={styles.paginationControls}>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className={styles.pageSizeSelect}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>

                    <div className={styles.pageButtons}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={styles.pageButton}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`${styles.pageButton} ${currentPage === pageNum ? styles.active : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={styles.pageButton}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Discrepancies Tab */}
        {activeTab === 'discrepancies' && (
          <div className={styles.discrepanciesTab}>
            <div className={styles.discrepanciesHeader}>
              <h3>
                <AlertTriangle size={20} />
                {t('courierInvoice.problemOrdersRequiringAttention')}
              </h3>
              {discrepancies.length > 0 && (
                <button onClick={downloadErrorReport} className={styles.secondaryButton}>
                  <Download size={18} />
                  {t('courierInvoice.downloadReport')}
                </button>
              )}
            </div>

            {discrepancies.length === 0 ? (
              <div className={styles.emptyState}>
                <CheckCircle size={48} className={styles.successIcon} />
                <h3>{t('courierInvoice.noDiscrepanciesFound')}</h3>
                <p>{t('courierInvoice.allOrdersMatched')}</p>
              </div>
            ) : (
              <div className={styles.discrepancyGroups}>
                {/* Not Found Orders */}
                {groupedDiscrepancies.notFound.length > 0 && (
                  <div className={styles.discrepancyGroup}>
                    <div 
                      className={styles.groupHeader}
                      onClick={() => setExpandedGroups(prev => ({ 
                        ...prev, 
                        notFound: !prev.notFound 
                      }))}
                    >
                      <div className={styles.groupTitle}>
                        <AlertCircle size={20} className={styles.errorIcon} />
                        <span>{t('courierInvoice.notFoundInSystem', { count: groupedDiscrepancies.notFound.length })}</span>
                      </div>
                      <div className={styles.groupActions}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReprocessGroup('not_found');
                          }}
                          className={styles.reprocessButton}
                        >
                          <RefreshCw size={16} />
                          {t('courierInvoice.reprocessAll')}
                        </button>
                        <ChevronDown 
                          size={20} 
                          className={expandedGroups.notFound ? styles.rotated : ''}
                        />
                      </div>
                    </div>
                    {expandedGroups.notFound && (
                      <div className={styles.groupContent}>
                        <div className={styles.discrepancyList}>
                          {groupedDiscrepancies.notFound.map(order => (
                            <div key={order.id} className={styles.discrepancyItem}>
                              <div className={styles.discrepancyInfo}>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.rowNumber')}:</span>
                                  <span>{order.rowNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.airwaybill')}:</span>
                                  <span className={styles.airwaybill}>{order.airwaybillNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.codAmount')}:</span>
                                  <span>{formatCurrency(order.codAmount)}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.error')}:</span>
                                  <span className={styles.errorText}>{order.errorMessage}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Already Invoiced Orders */}
                {groupedDiscrepancies.alreadyInvoiced.length > 0 && (
                  <div className={styles.discrepancyGroup}>
                    <div 
                      className={styles.groupHeader}
                      onClick={() => setExpandedGroups(prev => ({ 
                        ...prev, 
                        alreadyInvoiced: !prev.alreadyInvoiced 
                      }))}
                    >
                      <div className={styles.groupTitle}>
                        <AlertTriangle size={20} className={styles.warningIcon} />
                        <span>{t('courierInvoice.alreadyInvoicedGroup', { count: groupedDiscrepancies.alreadyInvoiced.length })}</span>
                      </div>
                      <ChevronDown 
                        size={20} 
                        className={expandedGroups.alreadyInvoiced ? styles.rotated : ''}
                      />
                    </div>
                    {expandedGroups.alreadyInvoiced && (
                      <div className={styles.groupContent}>
                        <div className={styles.discrepancyList}>
                          {groupedDiscrepancies.alreadyInvoiced.map(order => (
                            <div key={order.id} className={styles.discrepancyItem}>
                              <div className={styles.discrepancyInfo}>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.rowNumber')}:</span>
                                  <span>{order.rowNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.airwaybill')}:</span>
                                  <span className={styles.airwaybill}>{order.airwaybillNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.order')}:</span>
                                  {order.EcomOrder && (
                                    <Link 
                                      to={`/orders/${order.EcomOrder.id}`}
                                      className={styles.orderLink}
                                      target="_blank"
                                    >
                                      {order.EcomOrder.orderNumber}
                                      <ExternalLink size={14} />
                                    </Link>
                                  )}
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.error')}:</span>
                                  <span className={styles.errorText}>{order.errorMessage}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Ineligible Orders */}
                {groupedDiscrepancies.ineligible.length > 0 && (
                  <div className={styles.discrepancyGroup}>
                    <div 
                      className={styles.groupHeader}
                      onClick={() => setExpandedGroups(prev => ({ 
                        ...prev, 
                        ineligible: !prev.ineligible 
                      }))}
                    >
                      <div className={styles.groupTitle}>
                        <AlertCircle size={20} className={styles.infoIcon} />
                        <span>{t('courierInvoice.ineligibleOrdersGroup', { count: groupedDiscrepancies.ineligible.length })}</span>
                      </div>
                      <div className={styles.groupActions}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReprocessGroup('ineligible');
                          }}
                          className={styles.reprocessButton}
                        >
                          <RefreshCw size={16} />
                          {t('courierInvoice.reprocessAll')}
                        </button>
                        <ChevronDown 
                          size={20} 
                          className={expandedGroups.ineligible ? styles.rotated : ''}
                        />
                      </div>
                    </div>
                    {expandedGroups.ineligible && (
                      <div className={styles.groupContent}>
                        <div className={styles.discrepancyList}>
                          {groupedDiscrepancies.ineligible.map(order => (
                            <div key={order.id} className={styles.discrepancyItem}>
                              <div className={styles.discrepancyInfo}>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.rowNumber')}:</span>
                                  <span>{order.rowNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.airwaybill')}:</span>
                                  <span className={styles.airwaybill}>{order.airwaybillNumber}</span>
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.order')}:</span>
                                  {order.EcomOrder && (
                                    <Link 
                                      to={`/orders/${order.EcomOrder.id}`}
                                      className={styles.orderLink}
                                      target="_blank"
                                    >
                                      {order.EcomOrder.orderNumber}
                                      <ExternalLink size={14} />
                                    </Link>
                                  )}
                                </div>
                                <div className={styles.discrepancyRow}>
                                  <span className={styles.label}>{t('courierInvoice.error')}:</span>
                                  <span className={styles.errorText}>{order.errorMessage}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Flags Tab */}
        {activeTab === 'flags' && (
          <div className={styles.discrepanciesTab}>
            <div className={styles.discrepanciesHeader}>
              <h3>
                <Flag size={20} />
                {t('courierInvoice.flaggedOrders')}
              </h3>
              <div className={styles.flagsToolbar}>
                <div className={styles.flagStatusFilter}>
                  <Dropdown
                    value={flagStatusFilter}
                    onChange={handleFlagStatusChange}
                    options={[
                      { value: 'unresolved', label: t('courierInvoice.filterUnresolved') },
                      { value: 'resolved', label: t('courierInvoice.filterResolved') },
                      { value: 'all', label: t('courierInvoice.filterAll') }
                    ]}
                    allowClear={false}
                  />
                </div>
                {hasPermission('reprocess_courier_invoice') && (
                  <button onClick={handleRecheckFlags} className={styles.secondaryButton} disabled={rechecking}>
                    <RefreshCw size={18} className={rechecking ? styles.spinner : ''} />
                    {t('courierInvoice.recheckFlags')}
                  </button>
                )}
              </div>
            </div>

            <div className={styles.ordersFilters}>
              <div className={styles.filterGroup}>
                <Dropdown
                  value={flagFilters.deliveryStatus || null}
                  onChange={(value) => handleFlagFilterChange('deliveryStatus', value)}
                  options={[
                    { value: 'delivered', label: t('courierInvoice.deliveryDelivered') },
                    { value: 'returned', label: t('courierInvoice.deliveryReturned') },
                    { value: 'in_transit', label: t('courierInvoice.deliveryInTransit') },
                    { value: 'other', label: t('courierInvoice.deliveryOther') }
                  ]}
                  placeholder={t('courierInvoice.allDeliveryStatuses')}
                />
              </div>

              <div className={styles.filterGroup}>
                <Dropdown
                  value={flagFilters.reason || null}
                  onChange={(value) => handleFlagFilterChange('reason', value)}
                  options={[
                    'cod_mismatch',
                    'weight_mismatch',
                    'delivery_charge_mismatch',
                    'return_charge_mismatch',
                    'paid_amount_mismatch'
                  ].map(type => ({ value: type, label: t(`courierInvoice.flagReason_${type}`) }))}
                  placeholder={t('courierInvoice.allFlagReasons')}
                />
              </div>

              <div className={styles.searchBox}>
                <Search className={styles.searchIcon} size={18} />
                <input
                  type="text"
                  placeholder={t('courierInvoice.searchByAirwaybillOrOrder')}
                  value={flagFilters.search}
                  onChange={(e) => handleFlagFilterChange('search', e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>

            <DataTable
              data={flags}
              columns={flagColumns}
              actions={hasPermission('resolve_courier_invoice_flag') ? {
                width: '80px',
                render: (row) => !row.flagResolvedAt && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setResolveModal({ show: true, row, note: '', submitting: false });
                    }}
                    className={styles.actionButton}
                    title={t('courierInvoice.resolve')}
                    aria-label={t('courierInvoice.resolve')}
                  >
                    <CheckCircle size={16} />
                  </button>
                )
              } : undefined}
              pagination={flagsPagination}
              onPaginationChange={setFlagsPagination}
              onRowClick={openOrderModal}
              loading={flagsLoading}
              emptyMessage={t('courierInvoice.noFlags')}
              emptyIcon={<CheckCircle size={48} />}
            />
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className={styles.timelineTab}>
            <div className={styles.timeline}>
              <div className={styles.timelineItem}>
                <div className={styles.timelineMarker}>
                  <FileText size={16} />
                </div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineHeader}>
                    <h4>{t('courierInvoice.invoiceCreated')}</h4>
                    <span className={styles.timelineDate}>
                      {formatDateTime(invoice.createdAt)}
                    </span>
                  </div>
                  <p>{t('courierInvoice.invoiceInitializedBy', { user: invoice.uploadedBy?.name })}</p>
                  <div className={styles.timelineDetails}>
                    <span>{t('courierInvoice.invoiceNumberLabel')}: {invoice.invoiceNumber}</span>
                    <span>{t('courierInvoice.expectedOrders')}: {invoice.metadata?.totalOrdersExpected || invoice.totalOrders}</span>
                  </div>
                </div>
              </div>

              {invoice.metadata?.processingStartTime && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineMarker}>
                    <Clock size={16} />
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <h4>{t('courierInvoice.processingStarted')}</h4>
                      <span className={styles.timelineDate}>
                        {formatDateTime(invoice.metadata.processingStartTime)}
                      </span>
                    </div>
                    <p>{t('courierInvoice.startedProcessingInvoice')}</p>
                  </div>
                </div>
              )}

              {invoice.expectedChunks && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineMarker}>
                    <Package size={16} />
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <h4>{t('courierInvoice.chunksUploaded')}</h4>
                      <span className={styles.timelineDate}>
                        {t('courierInvoice.chunksCount', { 
                          received: invoice.receivedChunks, 
                          expected: invoice.expectedChunks 
                        })}
                      </span>
                    </div>
                    <p>{t('courierInvoice.orderDataUploadedInBatches')}</p>
                  </div>
                </div>
              )}

              {invoice.metadata?.processingEndTime && (
                <div className={styles.timelineItem}>
                  <div className={styles.timelineMarker}>
                    <CheckCircle size={16} className={styles.successIcon} />
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <h4>{t('courierInvoice.processingCompleted')}</h4>
                      <span className={styles.timelineDate}>
                        {formatDateTime(invoice.metadata.processingEndTime)}
                      </span>
                    </div>
                    <p>{t('courierInvoice.invoiceFinalizedWithStatus', { status: getStatusLabel(invoice.status) })}</p>
                    <div className={styles.timelineDetails}>
                      <span>{t('courierInvoice.totalOrders')}: {summary?.totalOrders || 0}</span>
                      <span>{t('courierInvoice.matched')}: {summary?.matchedOrders || 0}</span>
                      <span>{t('courierInvoice.processingTimeDuration', { 
                        seconds: Math.round((new Date(invoice.metadata.processingEndTime) - new Date(invoice.metadata.processingStartTime)) / 1000)
                      })}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.timelineItem}>
                <div className={styles.timelineMarker}>
                  <Calendar size={16} />
                </div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineHeader}>
                    <h4>{t('courierInvoice.lastUpdated')}</h4>
                    <span className={styles.timelineDate}>
                      {formatDateTime(invoice.updatedAt)}
                    </span>
                  </div>
                  <p>{t('courierInvoice.mostRecentModification')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className={styles.modalOverlay} onClick={() => setShowOrderModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{t('courierInvoice.orderDetails')}</h3>
              <button onClick={() => setShowOrderModal(false)} className={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.orderDetailsGrid}>
                <div className={styles.detailsSection}>
                  <h4>{t('courierInvoice.invoiceData')}</h4>
                  <div className={styles.detailsList}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.airwaybillNumber')}:</span>
                      <span className={styles.detailValue}>{selectedOrder.airwaybillNumber}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.codAmount')}:</span>
                      <span className={styles.detailValue}>{formatCurrency(selectedOrder.codAmount)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.courierCharge')}:</span>
                      <span className={styles.detailValue}>{formatCurrency(selectedOrder.courierCharge)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.weight')}:</span>
                      <span className={styles.detailValue}>{selectedOrder.invoiceWeight ?? '—'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.amountPayable')}:</span>
                      <span className={styles.detailValue}>{formatCurrency(selectedOrder.amountPayable)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.deliveryStatus')}:</span>
                      <span className={styles.detailValue}>{selectedOrder.deliveryStatus}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('courierInvoice.rowNumber')}:</span>
                      <span className={styles.detailValue}>{selectedOrder.rowNumber}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.detailsSection}>
                  <h4>{t('courierInvoice.systemOrderData')}</h4>
                  {selectedOrder.EcomOrder ? (
                    <div className={styles.detailsList}>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.orderNumber')}:</span>
                        <Link 
                          to={`/orders/${selectedOrder.EcomOrder.id}`}
                          className={styles.orderLink}
                          target="_blank"
                        >
                          {selectedOrder.EcomOrder.orderNumber}
                          <ExternalLink size={14} />
                        </Link>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.customer')}:</span>
                        <span className={styles.detailValue}>{selectedOrder.EcomOrder.customerName}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.shop')}:</span>
                        <span className={styles.detailValue}>{selectedOrder.EcomOrder.Shop?.name || 'N/A'}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.orderStatus')}:</span>
                        <span className={styles.detailValue}>
                          {selectedOrder.EcomOrder.OrderStatus ? (
                            <span
                              className={styles.orderStatusBadge}
                              style={{
                                backgroundColor: `${selectedOrder.EcomOrder.OrderStatus.colorCode}20`,
                                color: selectedOrder.EcomOrder.OrderStatus.colorCode,
                                borderColor: `${selectedOrder.EcomOrder.OrderStatus.colorCode}40`
                              }}
                            >
                              {selectedOrder.EcomOrder.OrderStatus.label}
                            </span>
                          ) : 'N/A'}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.items')}:</span>
                        <span className={styles.detailValue}>
                          {formatOrderItems(selectedOrder.EcomOrder.orderItems)}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.weight')}:</span>
                        <span className={styles.detailValue}>{selectedOrder.EcomOrder.weight ?? '—'}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.grandTotal')}:</span>
                        <span className={styles.detailValue}>{formatCurrency(selectedOrder.EcomOrder.grandTotal)}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.createdDate')}:</span>
                        <span className={styles.detailValue}>
                          {selectedOrder.EcomOrder.createdAt
                            ? formatDateTime(selectedOrder.EcomOrder.createdAt)
                            : 'N/A'
                          }
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.createdBy')}:</span>
                        <span className={styles.detailValue}>
                          {selectedOrder.EcomOrder.createdBy?.name || 'N/A'}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>{t('courierInvoice.confirmedBy')}:</span>
                        <span className={styles.detailValue}>
                          {selectedOrder.EcomOrder.confirmedByUser?.name || '—'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <AlertCircle size={32} className={styles.errorIcon} />
                      <p>{t('courierInvoice.noMatchingOrderFound')}</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedOrder.errorMessage && (
                <div className={styles.errorSection}>
                  <AlertCircle size={16} />
                  <span>{selectedOrder.errorMessage}</span>
                </div>
              )}

              <div className={styles.matchStatusSection}>
                <span className={styles.detailLabel}>{t('courierInvoice.matchStatus')}:</span>
                <span
                  className={styles.matchBadge}
                  style={{ 
                    backgroundColor: `${matchStatusColors[selectedOrder.orderMatchStatus]}20`,
                    color: matchStatusColors[selectedOrder.orderMatchStatus]
                  }}
                >
                  {getMatchStatusLabel(selectedOrder.orderMatchStatus)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {trackingRow && (
        <AirwaybillTrackingModal
          orderId={trackingRow.EcomOrder.id}
          airwaybillNumber={trackingRow.airwaybillNumber}
          courierName={invoice.courierAccount?.accountName}
          onClose={() => setTrackingRow(null)}
        />
      )}

      {/* Resolve Flag Modal */}
      {resolveModal.show && resolveModal.row && (
        <div className={styles.modalOverlay} onClick={closeResolveModal}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <Flag size={24} className={styles.warningIcon} />
              <h3>{t('courierInvoice.resolveFlagTitle', { airwaybill: resolveModal.row.airwaybillNumber })}</h3>
            </div>
            <div className={styles.modalContent}>
              <label className={styles.label} htmlFor="flagResolveNote">{t('courierInvoice.resolveNote')}</label>
              <textarea
                id="flagResolveNote"
                value={resolveModal.note}
                onChange={(e) => setResolveModal(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('courierInvoice.resolveNotePlaceholder')}
                className={styles.noteTextarea}
                rows={4}
                autoFocus
              />
            </div>
            <div className={styles.modalActions}>
              <button onClick={closeResolveModal} className={styles.secondaryButton} disabled={resolveModal.submitting}>
                {t('courierInvoice.cancelButton')}
              </button>
              <button
                onClick={handleResolveFlag}
                className={styles.primaryButton}
                disabled={!resolveModal.note.trim() || resolveModal.submitting}
              >
                {t('courierInvoice.resolve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className={styles.modalOverlay} onClick={() => setConfirmModal({ show: false, type: '', title: '', message: '' })}>
          <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <AlertCircle size={24} className={styles.warningIcon} />
              <h3>{confirmModal.title}</h3>
            </div>
            <div className={styles.modalContent}>
              <p>{confirmModal.message}</p>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={() => setConfirmModal({ show: false, type: '', title: '', message: '' })}
                className={styles.secondaryButton}
              >
                {t('courierInvoice.cancelButton')}
              </button>
              <button
                onClick={handleConfirmAction}
                className={confirmModal.type === 'delete' ? styles.dangerButton : styles.primaryButton}
              >
                {t('courierInvoice.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierInvoiceDetailsPage;